import { PRIORITY_META, type QuickAddParseDTO } from '@taskos/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { errorMessage, newIdempotencyKey, post } from '../../api/client';
import { invalidateTasks } from '../../api/queries';
import { useUI } from '../../app/ui';
import { useToast } from '../../components/toast';
import { Button, Kbd, Modal } from '../../components/ui';
import { formatDate, formatTime } from '../../lib/format';
import { useDebounced } from '../../lib/hooks';

/**
 * Single-line quick add ("Call HDFC tomorrow 5pm high priority remind 1h before").
 * Parsing happens on the server; low-confidence results always ask for confirmation.
 */
export function QuickAdd() {
  const ui = useUI();
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState('');
  const [confirm, setConfirm] = useState<QuickAddParseDTO | null>(null);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const idem = useRef(newIdempotencyKey());
  const debounced = useDebounced(text.trim(), 250);
  const parsed = useQuery({
    queryKey: ['quick-add', debounced],
    queryFn: () => post<QuickAddParseDTO>('/api/quick-add/parse', { text: debounced }),
    enabled: debounced.length > 1,
    staleTime: 10_000,
  });
  useEffect(
    () =>
      ui.registerQuickAdd(() => {
        const el = input.current;
        if (!el || el.offsetParent === null) return false; // hidden (other layout)
        el.focus();
        return true;
      }),
    [ui],
  );

  const create = async (p: QuickAddParseDTO) => {
    setSaving(true);
    try {
      const t = await post<{ ref: string }>(
        '/api/tasks',
        {
          title: p.title,
          priority: p.priority ?? 'MEDIUM',
          taskType: p.due.mode === 'NONE' ? 'ANYTIME' : 'FIXED_DATE',
          visibility: 'TEAM',
          due: p.due,
          tags: p.tags,
          reminders: p.reminderMinutesBefore ? [{ kind: 'BEFORE_DUE', offsetMinutes: p.reminderMinutesBefore, mode: 'ONCE', intensity: 'NORMAL' }] : [],
        },
        { idempotencyKey: idem.current },
      );
      toast.push({ kind: 'success', title: `✓ Created ${t.ref}`, message: p.title });
      setText('');
      setConfirm(null);
      idem.current = newIdempotencyKey();
      invalidateTasks(qc);
    } catch (e) {
      toast.push({ kind: 'error', title: 'Could not create the task', message: `${errorMessage(e)} — your text is still in the box.` });
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    const t = text.trim();
    if (!t || saving) return;
    // Always use a parse of the exact current text.
    let p = parsed.data && debounced === t ? parsed.data : null;
    if (!p) {
      try {
        p = await post<QuickAddParseDTO>('/api/quick-add/parse', { text: t });
      } catch (e) {
        toast.push({ kind: 'error', title: 'Could not read that', message: errorMessage(e) });
        return;
      }
    }
    if (p.confidence === 'LOW' || !p.title) setConfirm(p);
    else await create(p);
  };

  const preview = parsed.data && debounced === text.trim() ? parsed.data : null;
  return (
    <div className="stack sm" style={{ flex: 1, minWidth: 0, maxWidth: 640 }}>
      <form
        className="search-box"
        style={{ maxWidth: 'none' }}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Plus size={16} aria-hidden />
        <input
          ref={input}
          className="input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Quick add: Call HDFC tomorrow 5pm high priority"
          aria-label="Quick add a task"
          maxLength={500}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setText('');
              input.current?.blur();
            }
          }}
        />
        <Kbd>Q</Kbd>
      </form>
      {preview && text.trim() && (
        <div className="meta" aria-live="polite" style={{ paddingLeft: 4 }}>
          <Sparkles size={13} aria-hidden />
          <span>
            <strong>{preview.title || '(no title)'}</strong>
          </span>
          {preview.resolvedDate && <span>📅 {formatDate(preview.resolvedDate)}</span>}
          {preview.time && <span>🕐 {formatTime(preview.time)}</span>}
          {preview.priority && <span>{PRIORITY_META[preview.priority].emoji} {PRIORITY_META[preview.priority].label}</span>}
          {preview.reminderMinutesBefore && <span>🔔 {preview.reminderMinutesBefore >= 60 ? `${preview.reminderMinutesBefore / 60}h` : `${preview.reminderMinutesBefore}m`} before</span>}
          {preview.confidence === 'LOW' && <span className="badge warning">Check</span>}
        </div>
      )}
      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title="Please confirm"
        size="narrow"
        footer={
          <>
            <Button
              onClick={() => {
                setConfirm(null);
                ui.openTaskForm({
                  mode: 'create',
                  initial: {
                    title: confirm?.title ?? text,
                    priority: confirm?.priority ?? 'MEDIUM',
                    taskType: confirm?.due.mode === 'NONE' ? 'ANYTIME' : 'FIXED_DATE',
                    due: confirm?.due ?? { mode: 'NONE' },
                    tags: confirm?.tags ?? [],
                  },
                });
                setText('');
              }}
            >
              Edit details
            </Button>
            <Button variant="primary" disabled={!confirm?.title} loading={saving} onClick={() => (confirm ? create(confirm) : undefined)}>
              Create anyway
            </Button>
          </>
        }
      >
        {confirm && (
          <div className="stack sm">
            {confirm.warnings.map((w) => (
              <div key={w} className="inline-alert warning small">
                ⚠ {w}
              </div>
            ))}
            <dl className="kv">
              <dt>Title</dt>
              <dd>{confirm.title || <span className="error-text">Missing</span>}</dd>
              <dt>Due</dt>
              <dd>
                {confirm.resolvedDate ? formatDate(confirm.resolvedDate, true) : 'No date'}
                {confirm.time && ` · ${formatTime(confirm.time)}`}
              </dd>
              <dt>Priority</dt>
              <dd>{confirm.priority ? PRIORITY_META[confirm.priority].label : 'Medium'}</dd>
              {confirm.reminderMinutesBefore && (
                <>
                  <dt>Reminder</dt>
                  <dd>{confirm.reminderMinutesBefore} minutes before</dd>
                </>
              )}
            </dl>
          </div>
        )}
      </Modal>
    </div>
  );
}
